import dayjs from 'dayjs';
import { chain, isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetAllUserActions, useParamQuery } from 'src/hooks';
import { formatQuery, lang } from 'src/utils';

export const useListUserActionsPage = () => {
  const location = useLocation();

  const { activeFilters, onChangeParams } = useParamQuery({
    'timestamp.from': [dayjs().startOf('day').toISOString()],
    'timestamp.to': [dayjs().endOf('day').toISOString()],
    pageSize: ['100'],
    sort: ['-timestamp'],
  });

  const {
    data: userActions,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetAllUserActions(formatQuery(location.search));

  const [activeColumn, setActiveColumn] = useState('');

  const onChangedParams = (label: string, values: string | string[]) => {
    if (label === 'Date range') {
      const startDate = values[0];
      const endDate = values[1];

      onChangeParams('timestamp.from', startDate || []);
      onChangeParams('timestamp.to', endDate || []);
    } else if (label === 'collection.list') {
      onChangeParams(label, chain(values).split(',').compact().value());
    } else {
      onChangeParams(label, values);
    }
  };

  const [sorting, setSorting] = useState({
    columnKey: 'timestamp',
    order: 'desc',
  });

  const getNextActionTrackingPage = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const handleSort = (columnKey: string, order: string) => {
    const sortKey = columnKey === 'user' ? '*user' : columnKey;

    setSorting(() => ({
      columnKey,
      order,
    }));
    setActiveColumn(columnKey); // Keep original column key here

    onChangeParams('sort', [`${order === 'asc' ? sortKey : `-${sortKey}`}`]);
  };

  const columns = [
    { label: 'ID', key: 'id', sortIcon: true },
    { label: 'Changed by', key: 'user', sortIcon: true },
    { label: lang.role, key: 'role', sortIcon: true },
    { label: lang.collection, key: 'collection', sortIcon: true },
    { label: lang.changesMade, key: 'description', sortIcon: false },
    {
      label: lang.lastModified,
      key: 'timestamp',
      sortIcon: true,
    },
  ];

  return {
    userActions,
    isLoading,
    hasActiveFilters: !isEmpty(activeFilters),
    getNextActionTrackingPage,
    onChangeParams: onChangedParams,
    columns,
    handleSort,
    activeColumn,
    sorting,
    hasNextPage,
  };
};
